import { useEffect, useMemo, useState } from "react"
import { Helmet, HelmetProvider } from "react-helmet-async"
import ReactMarkdown from "react-markdown"
import { useParams } from "react-router-dom"
import api from "../api"
import { SurveyType } from "../common/constants"
import { Each } from "../common/Each"
import Breadcrumb from "../components/Breadcrumb"
import HeaderFooterLayout from "../components/layouts/HeaderFooterLayout"
import { formatDateV2, formatTimeV2, range } from "../utils"
import styles from "./StudentSurvey.module.css"

const StudentSurvey = () => {

    const { survey_id, students_id } = useParams()
    const [loading, setLoading] = useState(false)
    const [survey, setSurvey] = useState(null)

    const groups = useMemo(() => {
        if (!survey) {
            return []
        }

        const g = {}
        const { content, userAnswers } = survey

        for (const original of content) {
            const question = { ...original, options: original.options?.map(option => ({ ...option })) }
            if (!g[question.group]) {
                g[question.group] = []
            }

            const userAnswer = userAnswers.find(ua => ua.id === question.id)
            if (!userAnswer) {
                continue
            }

            switch (question.type) {
                case SurveyType.Open:
                    question.userAnswer = userAnswer.value ?? userAnswer.answer ?? ''; break
                case SurveyType.SingleChoice:
                    {
                        const optionIndex = Number.isInteger(userAnswer.answerId) ? userAnswer.answerId : null
                        const option = optionIndex !== null ? question.options?.[optionIndex] : undefined
                        if (option) {
                            option.selected = true
                            option.answerValue = userAnswer.answer?.answerValue
                            option.type = userAnswer.answer?.type
                        }
                    }
                    break
                case SurveyType.Numeric:
                    question.userAnswer = { id: userAnswer.answerId, value: userAnswer.answer }; break
                case SurveyType.MultipleChoice:
                    for (let i = 0; i < question.options.length; i++) {
                        const ua = userAnswer.answers?.find(ua => ua.id === i)
                        if (ua) {
                            question.options[i].selected = true
                            question.options[i].answerValue = ua.answerValue;
                            question.options[i].type = ua.type;
                        }
                    }
                    break
                default: break
            }
            g[question.group].push(question)
        }

        return g
    }, [survey])

    useEffect(() => {
        const getSurvey = async () => {
            setLoading(true)
            try {
                const survey = await api.get(`/admin/surveys/${survey_id}/users/${students_id}`)
                setSurvey(survey)
            } catch (e) {
                console.error(e)
            }
            setLoading(false)
        }
        getSurvey()
    }, [survey_id, students_id])

    return (
        <HeaderFooterLayout>
            <HelmetProvider>
                <Helmet>
                    <title>Sondaggio</title>
                </Helmet>
            </HelmetProvider>
            <div className={styles.container}>
                <div className={styles.section}>
                    <div className={styles.sectionInner}>
                        <div className={styles.back}>
                            <Breadcrumb items={[{ label: "Questionari", to: "/surveys" }, { label: survey?.title || "Risultati", to: `/surveys/${survey_id}` }, { label: "Risposte del partecipante" }]} />
                        </div>
                        {
                            !loading &&
                            <div className={styles.header}>
                                <div className={styles.userInfo}>
                                    <div className={styles.username}>
                                        {survey?.user?.name} {survey?.user?.surname}
                                    </div>
                                    <div className={styles.date}>
                                        {formatDateV2(survey?.created_at, "dd/MM/yyyy")} - {formatTimeV2(survey?.created_at)}
                                    </div>
                                </div>
                            </div>
                        }
                    </div>
                    <div className={styles.sectionInner}>
                        <Each
                            of={Object.keys(groups)}
                            render={(group) => (
                                <div className={styles.group}>
                                    <div className={styles.groupName}>{group}</div>
                                    <Each
                                        of={groups[group]}
                                        render={(question) => (
                                            <div className={styles.groupContent}>
                                                <div className={styles.title}><ReactMarkdown>{question.text}</ReactMarkdown></div>
                                                {
                                                    question.type === SurveyType.SingleChoice &&
                                                    <Each
                                                        of={question.options}
                                                        render={(option, index) => (
                                                            <div className={styles.answerWrapper}>
                                                                <div className={styles.answerContainer}>
                                                                    <div className={styles.answerSection}>
                                                                        <div className={
                                                                            option.selected ?
                                                                                `${styles.answer} ${styles.selected}` :
                                                                                styles.answer
                                                                        }>
                                                                            <ReactMarkdown>{`${option.value}`}</ReactMarkdown>
                                                                            <div>
                                                                                {option.type === "open" && option.answerValue}
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        )}
                                                    />
                                                }
                                                {
                                                    question.type === SurveyType.MultipleChoice &&
                                                    <Each
                                                        of={question.options}
                                                        render={(option, index) => (
                                                            <div className={styles.answerWrapper}>
                                                                <div className={styles.answerContainer}>
                                                                    <div className={styles.answerSection}>
                                                                        <div className={
                                                                            option.selected ?
                                                                                `${styles.answer} ${styles.selected}` :
                                                                                styles.answer
                                                                        }>
                                                                            <ReactMarkdown>{`${option.value}`}</ReactMarkdown>
                                                                            <div>
                                                                                {option.type === "open" && option.answerValue}
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        )}
                                                    />
                                                }
                                                {
                                                    question.type === SurveyType.Open &&
                                                    <div className={styles.openAnswer}>
                                                        {question.userAnswer?.trim() ? question.userAnswer : 'Nessuna risposta fornita'}
                                                    </div>
                                                }

                                                {
                                                    question.type === SurveyType.Numeric &&
                                                    <div className={styles.numeric}>
                                                        <Each
                                                            of={range(question.min, question.max, question.step).map(i => ({ value: i, selected: (question.userAnswer.id === i) }))}
                                                            render={(option, optionIdx) => (
                                                                <div className={
                                                                    option.selected ?
                                                                        `${styles.numericOption} ${styles.selected}` :
                                                                        styles.numericOption
                                                                }>
                                                                    {option.value}
                                                                </div>
                                                            )}
                                                        />
                                                    </div>
                                                }
                                            </div>
                                        )}
                                    />

                                </div>
                            )}
                        />
                    </div>
                </div>
            </div >
        </HeaderFooterLayout >
    )

}

export default StudentSurvey